import { scry, scryImageUrl } from "../lib/scryClient";
import { toast } from "@/components/ui/use-toast";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Routes,
  Route,
  useNavigate,
  useLocation,
  useParams,
  Outlet,
} from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { auth, db } from "@/lib/firebase";
import { SignIn } from "@/auth/SignIn";
// REMOVE: addToWishlist (now handled inline with prepareImageForSave)
// import { addToWishlist } from "@/data/collection";
import PriceStrip from "@/components/PriceStrip";
import VariantsModal from "@/components/VariantsModal";
import QuickOracleModal from "@/components/QuickOracleModal";

/* ★ NEW imports for marketplace-like section */
import {
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  limit as qLimit,
  orderBy,
  query,
  where,
  type DocumentData,
  type QueryDocumentSnapshot,
  // ADD:
  addDoc,
  collection,
  serverTimestamp,
  startAfter,
} from "firebase/firestore";
import OfferButton from "@/components/OfferButton";
import { haversineMiles } from "@/lib/geo";
// ADD: same image helper used by AddToCollectionModal/Edit
import { prepareImageForSave } from "@/lib/cardImage";
import FollowButton from "@/components/follow/FollowButton";

/* ---------- Types & helpers ---------- */
type ScryfallCard = {
  id: string;
  oracle_id?: string;
  name: string;
  set?: string;
  set_name?: string; // <-- added so we can pass to wishlist safely
  collector_number?: string;
  released_at?: string;
  image_uris?: { small?: string; normal?: string; large?: string };
  card_faces?: Array<{
    image_uris?: { small?: string; normal?: string; large?: string };
  }>;
  prices?: {
    usd?: string | null;
    usd_foil?: string | null;
    usd_etched?: string | null;
  };
  digital?: boolean;
  // Help VariantsModal avoid an extra fetch
  prints_search_uri?: string;
};

function getImageUrl(c: ScryfallCard): string {
  const face = c.card_faces?.[0]?.image_uris;
  return (
    face?.normal ||
    c.image_uris?.normal ||
    face?.large ||
    c.image_uris?.large ||
    face?.small ||
    c.image_uris?.small ||
    ""
  );
}
function parseDate(d?: string): number {
  if (!d) return 0;
  const t = Date.parse(d);
  return Number.isFinite(t) ? t : 0;
}
function hasImages(c: ScryfallCard) {
  return Boolean(c.image_uris || c.card_faces?.[0]?.image_uris);
}
/** Prefer newer, non-digital, with images */
function isBetterCandidate(a: ScryfallCard, b: ScryfallCard): boolean {
  const aDate = parseDate(a.released_at);
  const bDate = parseDate(b.released_at);
  if (aDate !== bDate) return aDate > bDate;

  const aNonDigital = a.digital ? 0 : 1;
  const bNonDigital = b.digital ? 0 : 1;
  if (aNonDigital !== bNonDigital) return aNonDigital > bNonDigital;

  const aHasImg = hasImages(a) ? 1 : 0;
  const bHasImg = hasImages(b) ? 1 : 0;
  if (aHasImg !== bHasImg) return aHasImg > bHasImg;

  const an = Number(a.collector_number ?? 0);
  const bn = Number(b.collector_number ?? 0);
  return an >= bn;
}

/** Very small in-file search util (Scryfall) */
const SCRY_DEBOUNCE_MS = 400;
const SCRY_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

async function scryfallSearch(
  q: string,
  nextUrl?: string,
  signal?: AbortSignal,
): Promise<{
  cards: ScryfallCard[];
  next: string | null;
  warnings?: string[];
}> {
  const url = nextUrl
    ? nextUrl
    : `https://api.scryfall.com/cards/search?q=${encodeURIComponent(q)}&unique=cards&order=released&dir=desc`;
  const res = await fetch(url, { signal, headers: { Accept: "application/json" } });
  if (res.status === 429) {
    const ra = res.headers.get("retry-after");
    const sec = Number.isFinite(parseInt(String(ra), 10)) ? parseInt(String(ra), 10) : 5;
    const err = new Error(`Rate limited by Scryfall. Try again in ~${sec}s`);
    (err as any).code = "rate-limit";
    (err as any).retryAfterMs = sec * 1000;
    throw err;
  }
  if (!res.ok) throw new Error(`Search failed (${res.status})`);
  const json = await res.json();
  const cards: ScryfallCard[] = (json.data || []) as ScryfallCard[];
  return {
    cards,
    next: json.has_more ? json.next_page : null,
    warnings: json.warnings,
  };
}

/* ---------- Small helpers for marketplace-like hits ---------- */
type MarketHit = {
  key: string; // ownerUid:docId
  docId: string;
  ownerUid: string;
  username: string;
  ownerAvatar: string | null;
  name: string;
  image?: string | null;
  set?: string | null;
  number?: string | null;
  foil?: boolean;
  price?: number | null;
  status: "sale" | "trade";
  scryfall_id?: string | null;
  distance?: number; // Infinity if not computable
};

function scryfallImgFallback(scryId: string) {
  return ` + scryImageUrl(scryId, "normal") + `;
}

/* ---------- Page ---------- */

export default function SearchPage() {
  const [q, setQ] = useState("");
  const [pending, setPending] = useState(false);
  const [byOracle, setByOracle] = useState<Map<string, ScryfallCard>>(
    new Map(),
  );
  const [next, setNext] = useState<string | null>(null);

  // Inline info/warning/error message under the search field
  const [msg, setMsg] = useState<string | null>(null);

  const acRef = useRef<AbortController | null>(null);
  const scryCacheRef = useRef<
    Map<string, { cards: ScryfallCard[]; next: string | null; warnings?: string[]; ts: number }>
  >(new Map());
  const debounceRef = useRef<number | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const qNorm = useMemo(() => normalizeQuery(q), [q]);
  const canSearch = useMemo(() => qNorm.length >= 3, [qNorm]);

  function upsertBatch(cards: ScryfallCard[]) {
    setByOracle((prev) => {
      const map = new Map(prev);
      for (const c of cards) {
        const key = c.oracle_id || c.name.toLowerCase();
        const existing = map.get(key);
        if (!existing || isBetterCandidate(c, existing)) {
          map.set(key, c);
        }
      }
      return map;
    });
  }

  useEffect(() => {
    if (!canSearch) {
      setByOracle(new Map());
      setNext(null);
      setMsg(null); // clear inline msg when query too short
      return;
    }
    setMsg(null); // clear inline msg at start

    // Abort any in-flight request
    acRef.current?.abort();
    const ac = new AbortController();
    acRef.current = ac;

    // Clear any pending debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }

    // Serve from cache if fresh
    const cached = scryCacheRef.current.get(qNorm);
    const now = Date.now();
    if (cached && now - cached.ts < SCRY_CACHE_TTL_MS) {
      setByOracle(new Map());
      upsertBatch(cached.cards);
      setNext(cached.next);
      if (cached.warnings?.length) setMsg(cached.warnings.join(" "));
      setPending(false);
      return () => ac.abort();
    }

    setPending(true);
    debounceRef.current = window.setTimeout(() => {
      scryfallSearch(qNorm, undefined, ac.signal)
        .then(({ cards, next, warnings }) => {
          setByOracle(new Map());
          upsertBatch(cards);
          setNext(next);
          if (warnings?.length) setMsg(warnings.join(" "));
          // cache result
          scryCacheRef.current.set(qNorm, {
            cards,
            next,
            warnings,
            ts: Date.now(),
          });
        })
        .catch((e: any) => {
          if (e.name === "AbortError") return;
          if (e?.code === "rate-limit") {
            setMsg(e.message || "Rate limited. Please wait a few seconds.");
          } else {
            setMsg(e?.message || "Search failed");
          }
        })
        .finally(() => {
          if (acRef.current === ac) setPending(false);
        });
    }, SCRY_DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      ac.abort();
    };
  }, [qNorm, canSearch]);

  async function onLoadMore() {
    if (!next) return;
    setPending(true);
    try {
      const ac = new AbortController();
      acRef.current = ac;
      const { cards: more, next: n, warnings } = await scryfallSearch("", next, ac.signal);
      upsertBatch(more);
      setNext(n);
      if (warnings?.length) setMsg(warnings.join(" "));
    } catch (e: any) {
      const m =
        e?.code === "rate-limit"
          ? e?.message || "Rate limited. Try again shortly."
          : e?.message ?? "Could not load more";
      setMsg(m);
    } finally {
      setPending(false);
    }
  }

  async function onHeart(c: ScryfallCard, e?: React.MouseEvent) {
    e?.stopPropagation();
    if (!auth.currentUser) {
      toast({ description: "Sign in to save wants." });
      return;
    }
    try {
      const uid = auth.currentUser.uid;

      // Use the same image pipeline (no file/upload here → falls back to Scryfall art)
      const img = await prepareImageForSave({
        uid,
        card: {
          id: c.id,
          name: c.name,
          image_uris: (c as any).image_uris ?? null,
          card_faces: (c as any).card_faces ?? null,
        },
        file: null,
        uploadedUrl: null,
      });

      await addDoc(collection(db, "users", uid, "wishlist"), {
        scryfall_id: c.id,
        oracle_id: c.oracle_id ?? null,
        name: c.name,
        set: c.set ?? null,
        number: c.collector_number ?? null,
        image: img.image, // string URL or Scryfall fallback
        custom_image_url: img.custom_image_url ?? null,
        custom_image_path: img.custom_image_path ?? null,
        createdAt: serverTimestamp(),
      });

      toast({ description: `Added “${c.name}” to Wants ✓` });
    } catch (err: any) {
      toast({ description: err?.message ?? "Could not add to wants" });
    }
  }

  /* Image click → Oracle (always top modal) */
  function openOracle(c: ScryfallCard) {
    navigate(`/search/oracle/${encodeURIComponent(c.id)}`, {
      state: { backgroundLocation: location },
    });
  }

  /* Tile click (or “+”) → Variants: user selects exact printing there */
  function openVariants(c: ScryfallCard) {
    // Pass a baseCard with oracle_id and prints_search_uri so the modal can list variants without refetch
    navigate(`/search/card/${encodeURIComponent(c.id)}`, {
      state: {
        backgroundLocation: location,
        baseCard: {
          id: c.id,
          oracle_id: c.oracle_id ?? null,
          name: c.name,
          prints_search_uri: (c as any).prints_search_uri ?? null,
          image_uris: c.image_uris ?? null,
          card_faces: c.card_faces ?? null,
        },
      },
    });
  }

  const tiles = useMemo(
    () =>
      Array.from(byOracle.values()).sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
      ),
    [byOracle],
  );

  /* ===================== NEW: “Available via Marketplace” section ===================== */
  const [marketHits, setMarketHits] = useState<MarketHit[]>([]);
  const [mhPending, setMhPending] = useState(false);
  const [myLatLng, setMyLatLng] = useState<{ lat: number; lng: number } | null>(
    null,
  );
  // Collapsible: closed by default; user must click to open
  const [marketOpen, setMarketOpen] = useState(false);

  // Load my location once (for distances). Completely optional for UI.
  useEffect(() => {
    (async () => {
      const uid = auth.currentUser?.uid;
      if (!uid) return;
      try {
        const snap = await getDoc(doc(db, "users", uid));
        const v = snap.data() as any;
        const lat = v?.location?.lat ?? null;
        const lng = v?.location?.lng ?? null;
        if (typeof lat === "number" && typeof lng === "number") {
          setMyLatLng({ lat, lng });
        }
      } catch {
        // ignore
      }
    })();
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadMarketplace() {
      const needle = qNorm.toLowerCase();
      if (needle.length < 3) {
        setMarketHits([]);
        return;
      }

      setMhPending(true);

      // Configurable scan parameters
      const PAGE_LIMIT = 100;     // docs per page
      const MAX_PAGES = 6;        // safety cap (100 * 6 = 600 docs scanned max)
      const MAX_RESULTS = 120;    // cap results kept/displayed (then we keep top 120 distance-sorted)

      // Single query pattern (status in ["sale","trade"])
      let lastDoc: QueryDocumentSnapshot<DocumentData> | null = null;
      let page = 0;

      // Cache user profiles to avoid multiple fetches
      const profileCache = new Map<string, { username: string; avatar: string | null; lat: number | null; lng: number | null }>();

      const uidMe = auth.currentUser?.uid ?? null;
      const hits: MarketHit[] = [];
      const seen = new Set<string>();

      function nameMatches(rawName: any): boolean {
        const name = (rawName ?? "").toString();
        if (!name) return false;
        const nameNorm = normalizeQuery(name).toLowerCase();
        return nameNorm.includes(needle);
      }

      try {
        while (!cancelled && page < MAX_PAGES) {
          const qBase = query(
            collectionGroup(db, "cards"),
            where("status", "in", ["sale", "trade"]),
            orderBy("createdAt", "desc"),
            lastDoc ? startAfter(lastDoc) : undefined!,
            qLimit(PAGE_LIMIT),
          );

            // NOTE: typescript may complain about undefined! startAfter – safe cast
          const snap = await getDocs(qBase);
          if (cancelled) break;
          if (snap.empty) break;

          for (const d of snap.docs) {
            if (cancelled) break;
            const v = d.data() as any;

            // Quick skip if name doesn't match
            if (!nameMatches(v?.name)) continue;

            // Derive owner UID from path users/{uid}/cards/{cardId}
            const parts = d.ref.path.split("/");
            const ownerUid = parts.length >= 4 ? parts[1] : null;
            if (!ownerUid) continue;
            if (uidMe && ownerUid === uidMe) continue;

            const key = `${ownerUid}:${d.id}`;
            if (seen.has(key)) continue;
            seen.add(key);

            // Profile (cached)
            if (!profileCache.has(ownerUid)) {
              try {
                const ps = await getDoc(doc(db, "users", ownerUid));
                if (ps.exists()) {
                  const p = ps.data() as any;
                  profileCache.set(ownerUid, {
                    username: p?.username || p?.display_name || ownerUid.slice(0, 6),
                    avatar: p?.avatar_url ?? null,
                    lat: typeof p?.location?.lat === "number" ? p.location.lat : null,
                    lng: typeof p?.location?.lng === "number" ? p.location.lng : null,
                  });
                } else {
                  profileCache.set(ownerUid, {
                    username: ownerUid.slice(0, 6),
                    avatar: null,
                    lat: null,
                    lng: null,
                  });
                }
              } catch {
                profileCache.set(ownerUid, {
                  username: ownerUid.slice(0, 6),
                  avatar: null,
                  lat: null,
                  lng: null,
                });
              }
            }

            const prof = profileCache.get(ownerUid)!;

            // Compute distance (if both ends known)
            let distance: number | undefined;
            if (myLatLng && prof.lat != null && prof.lng != null) {
              distance = haversineMiles(myLatLng.lat, myLatLng.lng, prof.lat, prof.lng);
            }

            const status: MarketHit["status"] =
              v.status === "sale" || v.for_sale === true ? "sale" : "trade";

            hits.push({
              key,
              docId: d.id,
              ownerUid,
              username: prof.username,
              ownerAvatar: prof.avatar,
              name: (v?.name || "Card").toString(),
              image: (v?.custom_image_url ?? v?.image ?? null) || null,
              set: v?.set ?? null,
              number: v?.number ?? null,
              foil: Boolean(v?.foil),
              price: typeof v?.price === "number" ? v.price : null,
              status,
              scryfall_id: v?.scryfall_id ?? null,
              distance,
            });

            if (hits.length >= MAX_RESULTS) break;
          }

          lastDoc = snap.docs[snap.docs.length - 1];
          page += 1;

          if (hits.length >= MAX_RESULTS) break;
          if (snap.size < PAGE_LIMIT) break; // no more pages
        }

        if (cancelled) return;

        // Sort by distance (known first, then unknown)
        hits.sort((a, b) => {
          const da = a.distance == null ? Number.POSITIVE_INFINITY : a.distance;
          const db = b.distance == null ? Number.POSITIVE_INFINITY : b.distance;
          if (da !== db) return da - db;
          // secondary: name
          return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
        });

        setMarketHits(hits);
      } catch {
        if (!cancelled) setMarketHits([]);
      } finally {
        if (!cancelled) setMhPending(false);
      }
    }

    void loadMarketplace();
    return () => {
      cancelled = true;
    };
  }, [qNorm, myLatLng]);

  // Force the marketplace panel open when a tour is running and selector needed.
  // (Optional) If you have a tour context, you could auto-open when stepId === 'global-marketplace-section'.
  // For now we default it to open so the target is visible.
  useEffect(() => {
    setMarketOpen(true);
  }, []);

  return (
    <div className="flex flex-col h-full">
      <main
        className="mx-auto max-w-6xl p-6 space-y-4"
        data-tour="global-search-section"
      >
        {/* SEARCH BOX */}
        <Card
          data-tour="global-search-box"
          className="bg-gray-900 border-gray-700 p-3 text-white"
        >
          <div className="flex items-center gap-3">
            <input
              className="flex-1 rounded border border-gray-700 bg-black/40 px-3 py-2 text-white"
              placeholder="Search cards (e.g., Lightning Bolt, type:creature cmc<=3)"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              aria-label="Search cards"
            />
            {!auth.currentUser && <SignIn />}
            <Button variant="outline" disabled>
              {pending ? "Searching…" : "Search"}
            </Button>
          </div>
          {msg && <div className="text-sm text-amber-300 mt-2">{msg}</div>}
        </Card>

        {/* ALWAYS RENDER MARKETPLACE SECTION (stable tour target) */}
        <section className="space-y-2" data-tour="global-marketplace-section">
          <button
            type="button"
            className="w-full flex items-center justify-between rounded border border-gray-700 bg-black/30 px-3 py-2 text-white hover:border-amber-400/60"
            onClick={() => setMarketOpen((s) => !s)}
            aria-expanded={marketOpen}
            aria-controls="marketplace-collapse"
          >
            <span className="font-semibold">
              Available via Marketplace
              {marketHits.length ? ` (${marketHits.length})` : ""}
            </span>
            <span className="text-sm opacity-80">
              {marketOpen ? "▾" : "▸"}
            </span>
          </button>

          <div
            id="marketplace-collapse"
            // Keep mounted so selector exists; just toggle visibility
            style={{ display: marketOpen ? "block" : "none" }}
          >
            {mhPending && marketHits.length === 0 ? (
              <div className="text-slate-300 text-sm mt-2">
                Searching community listings…
              </div>
            ) : marketHits.length === 0 && qNorm.length < 3 ? (
              <div className="text-slate-400 text-sm mt-2">
                Enter at least 3 characters to see marketplace matches.
              </div>
            ) : marketHits.length === 0 ? (
              <div className="text-slate-300 text-sm mt-2">
                No listings found for this query.
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 mt-2">
                {marketHits.map((it) => (
                  <article
                    key={it.key}
                    className="rounded border border-gray-700 bg-black/30 hover:border-amber-400/60 transition"
                    title={it.name}
                  >
                    <div className="aspect-[3/4] bg-black flex items-center justify-center">
                      <img
                        src={
                          it.image ||
                          (it.scryfall_id
                            ? scryfallImgFallback(it.scryfall_id)
                            : "/images/sg-card-back.png")
                        }
                        alt={it.name}
                        className="h-full w-full object-cover"
                        loading="lazy"
                      />
                    </div>

                    <div className="p-2 space-y-1">
                      <div className="text-sm font-semibold text-white truncate">
                        {it.name}
                      </div>
                      <div className="text-xs opacity-70 truncate">
                        {(it.set || "")?.toUpperCase?.() || ""}
                        {it.number ? ` • #${it.number}` : ""}
                        {it.foil ? " • Foil" : ""}
                      </div>

                      <div className="pt-1">
                        <div className="flex items-center gap-2">
                          <button
                            className="shrink-0 rounded-full border border-gray-700 overflow-hidden"
                            onClick={() => navigate(`/${it.username}`)}
                            aria-label={`Seller ${it.username}`}
                            title={`@${it.username}`}
                          >
                            <img
                              src={
                                it.ownerAvatar || "/images/avatars/default.png"
                              }
                              alt={it.username}
                              className="h-6 w-6 object-cover"
                            />
                          </button>
                          <button
                            className="text-xs underline truncate"
                            onClick={() => navigate(`/${it.username}`)}
                            title={`@${it.username}`}
                          >
                            @{it.username}
                          </button>
                          <div className="ml-auto flex items-center gap-2">
                            <FollowButton targetUid={it.ownerUid} className="hidden sm:inline-flex" />
                            <div className="text-xs opacity-70">
                              {it.distance != null && Number.isFinite(it.distance)
                                ? `${it.distance.toFixed(1)} mi`
                                : "—"}
                            </div>
                          </div>
                        </div>
                      </div>

                      <PriceStrip scryfallId={it.scryfall_id || ""} compact />

                      <div className="flex items-center justify-between pt-1">
                        <span className="text-sm font-semibold text-amber-300">
                          {it.status === "sale"
                            ? it.price != null
                              ? `$${it.price.toFixed(2)}`
                              : "—"
                            : "Trade"}
                        </span>
                        <OfferButton
                          toUid={it.ownerUid}
                          toUsername={it.username}
                          card={{
                            cardId: it.docId,
                            cardOwnerUid: it.ownerUid,
                            name: it.name,
                            scryfall_id: it.scryfall_id || undefined,
                          }}
                          variant="outline"
                          size="sm"
                          className="border-amber-700 text-amber-200 hover:bg-amber-900/20"
                        >
                          Make offer
                        </OfferButton>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* ORIGINAL RESULTS (unchanged) */}
        <section>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {tiles.map((c) => {
              const img = getImageUrl(c);
              const fallbackUsd =
                c?.prices?.usd != null
                  ? Number(c.prices.usd)
                  : c?.prices?.usd_foil != null
                    ? Number(c.prices.usd_foil)
                    : c?.prices?.usd_etched != null
                      ? Number(c.prices.usd_etched)
                      : undefined;

              return (
                <article
                  key={`${c.oracle_id}-${c.id}`}
                  className="rounded border border-gray-700 bg-black/30 overflow-hidden cursor-pointer"
                  onClick={() => openVariants(c)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      openVariants(c);
                    }
                  }}
                  aria-label={`Open Variants for ${c.name}`}
                >
                  <div className="relative aspect-[3/4] bg-black">
                    {/* Image click → Oracle */}
                    <img
                      src={img}
                      alt={c.name}
                      className="h-full w-full object-cover"
                      onClick={(e) => {
                        e.stopPropagation();
                        openOracle(c);
                      }}
                    />
                  </div>

                  <div className="p-2 space-y-1">
                    <div
                      className="text-sm font-medium text-white truncate"
                      title={c.name}
                    >
                      {c.name}
                    </div>
                    <div className="text-xs opacity-70 truncate">
                      {(c.set || "").toUpperCase()} • #{c.collector_number ?? ""}
                    </div>

                    <PriceStrip
                      scryfallId={c.id}
                      fallbackUsd={fallbackUsd}
                      compact
                    />

                    <div className="flex items-center justify-between gap-2 pt-1">
                      {/* “+” now opens Variants so user can pick exact printing */}
                      <button
                        className="btn-pixel btn-pixel-primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          openVariants(c);
                        }}
                        aria-label={`Choose ${c.name} printing to add`}
                        title="Choose printing to add"
                      >
                        +
                      </button>

                      <button
                        className="btn-pixel btn-pixel-secondary"
                        onClick={(e) => onHeart(c, e)}
                        aria-label={`Add ${c.name} to wants`}
                        title="Add to Wants"
                      >
                        ♥
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>

          <div className="mt-4 flex items-center justify-between">
            <div className="text-sm opacity-80">
              {tiles.length} result{tiles.length === 1 ? "" : "s"}
            </div>
            {next && (
              <Button variant="outline" onClick={onLoadMore} disabled={pending}>
                {pending ? "Loading…" : "Load more"}
              </Button>
            )}
          </div>
        </section>

        {/* Modals / Routes */}
        <Routes>
          <Route path="card/:cardId" element={<VariantsModalRoute />} />
          <Route path="oracle/:cardId" element={<QuickOracleModal />} />
        </Routes>
      </main>

      {/* Allow nested /search/card/:cardId to render modal */}
      <Outlet />
    </div>
  );
}

/* Route wrapper for VariantsModal */
function VariantsModalRoute() {
  const { cardId } = useParams<{ cardId: string }>();
  const navigate = useNavigate();
  const location = useLocation() as any;
  const baseFromState = location?.state?.baseCard || null;
   if (!cardId) return null;

   return (
     <VariantsModal
       open
      // Prefer rich baseCard from state when available; fallback to id-only
      baseCard={(baseFromState && baseFromState.id === cardId ? baseFromState : { id: cardId }) as any}
       onClose={() => navigate("/search", { replace: false })}
     />
   );
}

// Normalize query so "and" and "&" are interchangeable
function normalizeQuery(input: string): string {
  return input
    .replace(/&/g, " and ")
    .replace(/\s+and\s+/gi, " and ") // collapse variants like "  and   "
    .replace(/\s+/g, " ")
    .trim();
}

